// MARK://dhk8/admin-shell/pages/modules/MenuDetailPage
// Ficha "ver-primero" de un menú del compositor (Gustavo 2026-06-23). Header con
// nombre + ventana horaria + pills (estado / publicado-borrador, o "Automático" si
// es el menú auto-armado del catálogo) + KPIs (secciones, productos) + Tabs
// (General, Secciones/Ítems, Historial). El tab General se ve primero; "Editar"
// abre el SectionedSheet con el MISMO formulario que el modal de edición que la
// lista usaba. Carga el menú por id con menus.getMenu(id); las secciones/productos
// se editan in-situ con los mismos clients (createSection/updateSection/addProduct…).
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Plus, Trash2, CheckCircle2, ChevronUp, ChevronDown } from 'lucide-react'

import * as menus from '../../lib/clients/menus.js'
import * as cat from '../../lib/clients/catalog.js'
import { requireTenant } from '../../lib/api.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import Sheet from '../../components/Sheet.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import EntityCombobox from '../../components/EntityCombobox.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'
import type { Product } from '../../lib/mock-data.js'

type Tab = 'general' | 'secciones' | 'historial'

export default function MenuDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()

  // El menú automático (id === tenant) es SINTÉTICO: no existe como registro, por lo
  // que getMenu(id) da 404 ("Menú no encontrado"). Viene compuesto en listMenus. Por eso
  // resolvemos el auto (y cualquier id que getMenu no resuelva) desde listMenus. El render
  // ya trata isAuto como read-only.
  const menu = useAsync(async (s) => {
    if (id === requireTenant()) {
      const found = (await menus.listMenus(s)).find((x) => x.id === id)
      if (found) return found
    }
    try {
      return await menus.getMenu(id, s)
    } catch (e) {
      const found = (await menus.listMenus(s)).find((x) => x.id === id)
      if (found) return found
      throw e
    }
  }, [id])
  const products = useAsync((s) => cat.listProducts(s))

  const [tab, setTab] = useState<Tab>('general')
  const [busy, setBusy] = useState(false)

  // edit-menu sheet (mismo form que el modal de edición de la lista)
  const [editing, setEditing] = useState(false)
  const [editMenuForm, setEditMenuForm] = useState({ name: '', daysOfWeek: [] as number[], startTime: '', endTime: '', active: true })
  const [editMenuError, setEditMenuError] = useState<string | null>(null)

  // rename-section sheet
  const [renamingSection, setRenamingSection] = useState<string | null>(null)
  const [renameSectionName, setRenameSectionName] = useState('')
  const [renameSectionError, setRenameSectionError] = useState<string | null>(null)

  // add-section / add-product sheets
  const [addingSection, setAddingSection] = useState(false)
  const [sectionName, setSectionName] = useState('')
  const [productFor, setProductFor] = useState<string | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [overridePrice, setOverridePrice] = useState('')
  const [sheetError, setSheetError] = useState<string | null>(null)

  const m = menu.data
  // El menú "automático" (armado del catálogo) tiene id === tenant y es read-only.
  const isAuto = m != null && m.id === requireTenant()

  const productName = (pid: string) => products.data?.find((p) => p.id === pid)?.name ?? pid
  const productPriceCents = (pid: string) => products.data?.find((p) => p.id === pid)?.priceCents ?? null

  // Búsqueda para EntityCombobox: filtra el catálogo ya cargado por nombre/SKU.
  async function searchProducts(q: string): Promise<Product[]> {
    const all = products.data ?? []
    const needle = q.trim().toLowerCase()
    const matches = needle
      ? all.filter((p) => p.name.toLowerCase().includes(needle) || p.sku.toLowerCase().includes(needle))
      : all
    return matches.slice(0, 50)
  }

  async function act(fn: () => Promise<unknown>, okMsg: string, errMsg: string) {
    setBusy(true)
    try { await fn(); toast(okMsg, 'success'); menu.reload() }
    catch (e) { toast(e instanceof Error ? e.message : errMsg, 'danger') }
    finally { setBusy(false) }
  }

  function openEdit() {
    if (!m) return
    setEditMenuForm({
      name: m.name,
      daysOfWeek: [...m.daysOfWeek],
      startTime: m.startTime ?? '',
      endTime: m.endTime ?? '',
      active: m.active,
    })
    setEditMenuError(null)
    setEditing(true)
  }

  async function saveEditMenu() {
    if (!m) return
    setEditMenuError(null)
    if (!editMenuForm.name.trim()) { setEditMenuError('El nombre es obligatorio.'); return }
    setBusy(true)
    try {
      await menus.updateMenu(m.id, {
        name: editMenuForm.name.trim(),
        daysOfWeek: editMenuForm.daysOfWeek,
        startTime: editMenuForm.startTime ? editMenuForm.startTime : null,
        endTime: editMenuForm.endTime ? editMenuForm.endTime : null,
        active: editMenuForm.active,
      })
      setEditing(false)
      toast('Menú actualizado', 'success')
      menu.reload()
    } catch (e) {
      setEditMenuError(e instanceof Error ? e.message : 'No se pudo actualizar el menú.')
    } finally { setBusy(false) }
  }

  async function saveRenameSection() {
    if (!m || !renamingSection) return
    setRenameSectionError(null)
    if (!renameSectionName.trim()) { setRenameSectionError('El nombre de la sección es obligatorio.'); return }
    setBusy(true)
    try {
      await menus.updateSection(m.id, renamingSection, { name: renameSectionName.trim() })
      setRenamingSection(null); setRenameSectionName('')
      toast('Sección renombrada', 'success')
      menu.reload()
    } catch (e) {
      setRenameSectionError(e instanceof Error ? e.message : 'No se pudo renombrar la sección.')
    } finally { setBusy(false) }
  }

  // Reordena una sección moviéndola arriba/abajo intercambiando su ordinal con el
  // vecino. El client soporta updateSection({ ordinal }); persistimos ambos.
  async function moveSection(sections: menus.MenuSection[], index: number, dir: -1 | 1) {
    if (!m) return
    const ordered = sections.slice().sort((a, b) => a.ordinal - b.ordinal)
    const target = index + dir
    if (target < 0 || target >= ordered.length) return
    const a = ordered[index]
    const b = ordered[target]
    setBusy(true)
    try {
      await menus.updateSection(m.id, a.id, { ordinal: b.ordinal })
      await menus.updateSection(m.id, b.id, { ordinal: a.ordinal })
      menu.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo reordenar la sección.', 'danger')
    } finally { setBusy(false) }
  }

  async function saveSection() {
    if (!m) return
    setSheetError(null)
    if (!sectionName.trim()) { setSheetError('El nombre de la sección es obligatorio.'); return }
    setBusy(true)
    try {
      // Nueva sección al final: ordinal = max(ordinals)+1 del menú.
      const nextOrdinal = m.sections.length ? Math.max(...m.sections.map((s) => s.ordinal)) + 1 : 0
      await menus.createSection(m.id, sectionName.trim(), nextOrdinal)
      setAddingSection(false); setSectionName('')
      toast('Sección creada', 'success')
      menu.reload()
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : 'No se pudo crear la sección.')
    } finally { setBusy(false) }
  }

  async function saveProduct() {
    if (!m || !productFor) return
    setSheetError(null)
    if (!selectedProduct) { setSheetError('Selecciona un producto.'); return }
    setBusy(true)
    try {
      const override = overridePrice.trim()
      await menus.addProduct(m.id, productFor, {
        productId: selectedProduct.id,
        ...(override ? { overridePriceCents: Math.round(Number(override) * 100) } : {}),
      })
      setProductFor(null); setSelectedProduct(null); setOverridePrice('')
      toast('Producto agregado', 'success')
      menu.reload()
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : 'No se pudo agregar el producto.')
    } finally { setBusy(false) }
  }

  const productCount = useMemo(() => (m ? m.sections.reduce((n, s) => n + s.products.length, 0) : 0), [m])
  const kpis = useMemo(() => {
    if (!m) return []
    return [
      { label: 'Secciones', value: m.sections.length },
      { label: 'Productos', value: productCount },
    ]
  }, [m, productCount])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/menus')} aria-label="Volver a menús"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Menú</h1><p className="adm-page__sub">Menús · ficha del menú</p></div>
        </div>
      </div>

      {asyncState(menu.loading, menu.error, menu.reload)}

      {m && (
        <EntityDetailPage
          title={m.name}
          subtitle={menus.scheduleLabel(m)}
          pills={isAuto ? (
            <span className="adm-badge" title="Menú automático armado de tu catálogo. Crea un menú nuevo desde la lista para personalizar horario y secciones.">Automático · catálogo</span>
          ) : (
            <>
              <StatusPill tone={lifecycleTone(m.active)} label={m.active ? 'Activo' : 'Inactivo'} />
              {m.publishedAt ? <StatusPill tone="ok" label="Publicado" /> : <StatusPill tone="info" label="Borrador" />}
            </>
          )}
          kpis={<SummaryStrip items={kpis} />}
          actions={isAuto ? (
            <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => navigate('/menus?new=1')}><Plus size={15} /> Crear menú personalizado</button>
          ) : (
            <>
              {!m.publishedAt && <button className="adm-btn adm-btn--sm" disabled={busy} onClick={() => void act(() => menus.publishMenu(m.id), 'Menú publicado', 'No se pudo publicar.')}><CheckCircle2 size={15} /> Publicar</button>}
              <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>
              <button className="adm-btn adm-btn--sm adm-btn--danger" disabled={busy} onClick={async () => { const ok = await confirm({ title: `¿Eliminar el menú "${m.name}"?`, message: 'Se eliminarán sus secciones y productos. Esta acción no se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' }); if (ok) { await act(() => menus.deleteMenu(m.id), 'Menú eliminado', 'No se pudo eliminar.'); navigate('/menus') } }}><Trash2 size={15} /> Eliminar</button>
            </>
          )}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'secciones', label: 'Secciones/Ítems', count: m.sections.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del menú"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre">{m.name}</Field>
              <Field label="Disponibilidad">{menus.scheduleLabel(m)}</Field>
              <Field label="Estado">{isAuto ? 'Automático (catálogo)' : (m.active ? 'Activo' : 'Inactivo')}</Field>
              <Field label="Publicación">{isAuto ? <span className="adm-muted">No aplica · se arma solo de tu catálogo</span> : (m.publishedAt ? `Publicado · ${new Date(m.publishedAt).toLocaleString('es-MX')}` : <span className="adm-muted">Borrador (sin publicar)</span>)}</Field>
              <Field label="Secciones">{m.sections.length}</Field>
              <Field label="Productos">{productCount}</Field>
              {isAuto && <div className="adm-banner">Menú automático armado de tu catálogo (productos y categorías). Para horarios o secciones personalizadas, crea un menú nuevo desde la lista.</div>}
            </div>
          )}

          {tab === 'secciones' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {isAuto && (
                <div className="adm-banner">Menú automático armado de tu catálogo (productos y categorías). Para horarios o secciones personalizadas, crea un menú nuevo desde la lista.</div>
              )}
              {m.sections.length === 0 && <div className="adm-card adm-card--pad"><div className="adm-empty">Este menú no tiene secciones todavía.</div></div>}
              {(() => { const orderedSections = m.sections.slice().sort((a, b) => a.ordinal - b.ordinal); return orderedSections.map((s, si) => (
                <div key={s.id} className="adm-card adm-card--pad0">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', gap: 8 }}>
                    <strong style={{ fontSize: 14, flex: 1, minWidth: 0 }}>{s.name}</strong>
                    {!isAuto && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy || si === 0} onClick={() => void moveSection(orderedSections, si, -1)} aria-label="Subir sección" title="Subir"><ChevronUp size={14} /></button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy || si === orderedSections.length - 1} onClick={() => void moveSection(orderedSections, si, 1)} aria-label="Bajar sección" title="Bajar"><ChevronDown size={14} /></button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={() => { setProductFor(s.id); setSelectedProduct(null); setOverridePrice(''); setSheetError(null) }}><Plus size={14} /> Producto</button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm" disabled={busy} onClick={() => { setRenamingSection(s.id); setRenameSectionName(s.name); setRenameSectionError(null) }} aria-label="Renombrar sección"><Pencil size={14} /></button>
                        <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={async () => { const ok = await confirm({ title: `¿Eliminar la sección "${s.name}"?`, message: 'Se quitarán también sus productos de este menú.', tone: 'danger', confirmLabel: 'Eliminar' }); if (ok) void act(() => menus.removeSection(m.id, s.id), 'Sección eliminada', 'No se pudo eliminar.') }} aria-label="Eliminar sección"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                  <div className="adm-list" style={{ borderTop: '1px solid var(--pos-separator)' }}>
                    {s.products.length === 0 && <div className="adm-empty" style={{ padding: 10 }}>Sin productos</div>}
                    {s.products.slice().sort((a, b) => a.ordinal - b.ordinal).map((p) => {
                      const basePrice = productPriceCents(p.productId)
                      const meta = p.overridePriceCents != null
                        ? `Precio especial ${mxn(p.overridePriceCents)}${basePrice != null ? ` · base ${mxn(basePrice)}` : ''}`
                        : basePrice != null ? `Precio del catálogo ${mxn(basePrice)}` : 'Precio del catálogo'
                      return (
                        <div key={p.id} className="adm-row">
                          <div className="adm-row__main">
                            <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span>{productName(p.productId)}</span>
                              {!p.visible && <StatusPill tone="neutral" label="Oculto" />}
                            </div>
                            <div className="adm-row__meta">{meta}</div>
                          </div>
                          {!isAuto && (
                            <div className="adm-row__tail">
                              <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" disabled={busy} onClick={async () => { const ok = await confirm({ title: `¿Quitar "${productName(p.productId)}" de la sección?`, tone: 'danger', confirmLabel: 'Quitar' }); if (ok) void act(() => menus.removeProduct(m.id, s.id, p.id), 'Producto quitado', 'No se pudo quitar.') }} aria-label="Quitar"><Trash2 size={14} /></button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )) })()}
              {!isAuto && (
                <div>
                  <button className="adm-btn adm-btn--sm" disabled={busy} onClick={() => { setAddingSection(true); setSectionName(''); setSheetError(null) }}><Plus size={14} /> Agregar sección</button>
                </div>
              )}
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={m.id} />}
        </EntityDetailPage>
      )}

      {/* Edit menu */}
      <SectionedSheet
        open={editing}
        title="Editar menú"
        onClose={() => { setEditing(false); setEditMenuError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEditMenu} disabled={busy || !editMenuForm.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {editMenuError && <div className="adm-banner adm-banner--danger" role="alert">{editMenuError}</div>}
        <FormSection title="Datos básicos">
          <label className="adm-field">
            <span className="adm-field__label">Nombre</span>
            <input className="adm-input" value={editMenuForm.name} onChange={(e) => setEditMenuForm({ ...editMenuForm, name: e.target.value })} placeholder="Menú principal" autoFocus />
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={editMenuForm.active} onChange={(e) => setEditMenuForm({ ...editMenuForm, active: e.target.checked })} />
            <span className="adm-field__label" style={{ margin: 0 }}>Activo</span>
          </label>
        </FormSection>

        <FormSection title="Disponibilidad">
          <div className="adm-field">
            <span className="adm-field__label">Días de la semana (vacío = todos)</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {menus.DAY_LABELS.map((d, i) => {
                const on = editMenuForm.daysOfWeek.includes(i)
                return (
                  <button
                    key={i}
                    type="button"
                    className={`adm-btn adm-btn--sm${on ? ' adm-btn--primary' : ' adm-btn--ghost'}`}
                    onClick={() => setEditMenuForm({ ...editMenuForm, daysOfWeek: on ? editMenuForm.daysOfWeek.filter((x) => x !== i) : [...editMenuForm.daysOfWeek, i] })}
                  >{d}</button>
                )
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="adm-field__label">Desde</span>
              <input className="adm-input" type="time" value={editMenuForm.startTime} onChange={(e) => setEditMenuForm({ ...editMenuForm, startTime: e.target.value })} />
            </label>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="adm-field__label">Hasta</span>
              <input className="adm-input" type="time" value={editMenuForm.endTime} onChange={(e) => setEditMenuForm({ ...editMenuForm, endTime: e.target.value })} />
            </label>
          </div>
        </FormSection>
      </SectionedSheet>

      {/* Rename section */}
      <Sheet
        open={renamingSection != null}
        title="Renombrar sección"
        onClose={() => { setRenamingSection(null); setRenameSectionError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setRenamingSection(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveRenameSection} disabled={busy || !renameSectionName.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </>}
      >
        {renameSectionError && <div className="adm-banner adm-banner--danger" role="alert">{renameSectionError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre de la sección</span>
          <input className="adm-input" value={renameSectionName} onChange={(e) => setRenameSectionName(e.target.value)} placeholder="Bebidas calientes" />
        </label>
      </Sheet>

      {/* Add section */}
      <Sheet
        open={addingSection}
        title="Nueva sección"
        onClose={() => { setAddingSection(false); setSheetError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAddingSection(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveSection} disabled={busy || !sectionName.trim()}>{busy ? 'Guardando…' : 'Crear sección'}</button>
        </>}
      >
        {sheetError && <div className="adm-banner adm-banner--danger" role="alert">{sheetError}</div>}
        <label className="adm-field">
          <span className="adm-field__label">Nombre de la sección</span>
          <input className="adm-input" value={sectionName} onChange={(e) => setSectionName(e.target.value)} placeholder="Bebidas calientes" />
        </label>
      </Sheet>

      {/* Add product */}
      <SectionedSheet
        open={productFor != null}
        title="Agregar producto a la sección"
        onClose={() => { setProductFor(null); setSelectedProduct(null); setOverridePrice(''); setSheetError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => { setProductFor(null); setSelectedProduct(null); setOverridePrice('') }}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveProduct} disabled={busy || !selectedProduct}>{busy ? 'Guardando…' : 'Agregar'}</button>
        </>}
      >
        {sheetError && <div className="adm-banner adm-banner--danger" role="alert">{sheetError}</div>}
        <FormSection title="Producto">
          <div className="adm-field">
            <span className="adm-field__label">Producto</span>
            <EntityCombobox<Product>
              value={selectedProduct}
              onChange={setSelectedProduct}
              fetchOptions={searchProducts}
              renderOption={(p) => <span>{p.name} · <span className="adm-muted">{p.sku} · {mxn(p.priceCents)}</span></span>}
              getOptionValue={(p) => p.id}
              getOptionLabel={(p) => p.name}
              placeholder="Buscar producto por nombre o SKU…"
              allowClear
            />
          </div>
          {selectedProduct && (
            <div className="adm-row__meta">Precio base del catálogo: {mxn(selectedProduct.priceCents)}</div>
          )}
        </FormSection>
        <FormSection title="Precio en este menú">
          <label className="adm-field">
            <span className="adm-field__label">Precio especial (opcional, MXN)</span>
            <input className="adm-input" inputMode="decimal" min={0} step="0.01" value={overridePrice} onChange={(e) => setOverridePrice(e.target.value)} placeholder={selectedProduct ? `Deja vacío para usar el precio base (${mxn(selectedProduct.priceCents)})` : 'Deja vacío para usar el precio del catálogo'} />
          </label>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
